import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import api from "../services/api";
import { useAppSettings } from "../contexts/AppSettingsContext";

const Settings = () => {
  const { refresh: refreshAppSettings } = useAppSettings();

  const hexLuminance = (hex) => {
    const h = hex.replace("#", "");
    if (h.length !== 6) return 0;
    const [r, g, b] = [0, 2, 4].map((i) => {
      const c = parseInt(h.slice(i, i + 2), 16) / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const applySidebarColor = (hex) => {
    document.documentElement.style.setProperty("--sidebar-bg", hex);
    const isLight = hexLuminance(hex) > 0.4;
    document.documentElement.style.setProperty(
      "--sidebar-text",
      isLight ? "#334155" : "rgba(255,255,255,0.80)",
    );
    document.documentElement.style.setProperty(
      "--sidebar-label",
      isLight ? "rgba(0,0,0,0.40)" : "rgba(255,255,255,0.45)",
    );
    document.documentElement.style.setProperty(
      "--sidebar-active-text",
      isLight ? "#0f172a" : "#ffffff",
    );
    document.documentElement.style.setProperty(
      "--sidebar-active",
      isLight ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.18)",
    );
  };

  const [settings, setSettings] = useState({
    app_name: "",
    app_icon: "",
    store_name: "",
    store_address: "",
    store_phone: "",
    tax_percentage: "0",
    min_stock_alert: "5",
    receipt_footer: "",
    sidebar_color: "",
    primary_color: "",
    sidebar_display: "both",
  });
  const [loading, setLoading] = useState(false);
  const iconInputRef = useRef(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await api.get("/settings");
      setSettings((prev) => ({ ...prev, ...res.data }));
    } catch (error) {
      toast.error("Gagal memuat pengaturan");
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put("/settings", settings);
      refreshAppSettings();
      toast.success("Pengaturan berhasil disimpan");
    } catch (error) {
      toast.error("Gagal menyimpan");
    } finally {
      setLoading(false);
    }
  };

  const handleIconUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 200 * 1024) {
      toast.error("Ukuran icon maksimal 200 KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) =>
      setSettings((s) => ({ ...s, app_icon: ev.target.result }));
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <h4 className="mb-4">Pengaturan</h4>

      <div className="row g-3 align-items-start">
        {/* === KOLOM KIRI === */}
        <div className="col-12 col-lg-6">
          {/* IDENTITAS APLIKASI */}
          <div className="card p-4 mb-3">
            <h6 className="fw-bold mb-3">Identitas Aplikasi</h6>
            <div className="mb-3">
              <label className="form-label">Nama Aplikasi</label>
              <input
                type="text"
                className="form-control"
                value={settings.app_name}
                placeholder="CarWash Pro"
                onChange={(e) => {
                  setSettings({ ...settings, app_name: e.target.value });
                  if (e.target.value) document.title = e.target.value;
                }}
              />
              <div className="form-text">
                Tampil di sidebar, tab browser, dan halaman login.
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Icon Aplikasi</label>
              <div className="d-flex align-items-center gap-3">
                <div
                  className="rounded d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: 64,
                    height: 64,
                    background: "#f1f5f9",
                    border: "2px dashed #cbd5e1",
                    overflow: "hidden",
                  }}
                >
                  {settings.app_icon ? (
                    <img
                      src={settings.app_icon}
                      alt="icon"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: "2rem" }}>🚗</span>
                  )}
                </div>
                <div className="flex-grow-1">
                  <input
                    ref={iconInputRef}
                    type="file"
                    accept="image/*"
                    className="d-none"
                    onChange={handleIconUpload}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary me-2"
                    onClick={() => iconInputRef.current?.click()}
                  >
                    Upload Gambar
                  </button>
                  {settings.app_icon && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() =>
                        setSettings((s) => ({ ...s, app_icon: "" }))
                      }
                    >
                      Hapus
                    </button>
                  )}
                  <div className="form-text mt-1">
                    Format: PNG/JPG/SVG, maks 200 KB
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-0">
              <label className="form-label">Tampilan Sidebar</label>
              <div className="d-flex gap-2 flex-wrap">
                {[
                  { val: "both", label: "Icon + Nama" },
                  { val: "icon_only", label: "Icon saja" },
                  { val: "name_only", label: "Nama saja" },
                ].map(({ val, label }) => (
                  <button
                    key={val}
                    type="button"
                    className={`btn btn-sm ${
                      settings.sidebar_display === val
                        ? "btn-primary"
                        : "btn-outline-secondary"
                    }`}
                    onClick={() =>
                      setSettings({ ...settings, sidebar_display: val })
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="form-text">
                Pilih apa yang ditampilkan di header sidebar.
              </div>
            </div>
          </div>

          {/* INFORMASI TOKO */}
          <div className="card p-4 mb-3">
            <h6 className="fw-bold mb-3">Informasi Toko</h6>
            <div className="mb-3">
              <label className="form-label">Nama Toko</label>
              <input
                type="text"
                className="form-control"
                value={settings.store_name}
                onChange={(e) =>
                  setSettings({ ...settings, store_name: e.target.value })
                }
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Alamat</label>
              <textarea
                className="form-control"
                rows="2"
                value={settings.store_address}
                onChange={(e) =>
                  setSettings({ ...settings, store_address: e.target.value })
                }
              />
            </div>
            <div className="mb-0">
              <label className="form-label">Telepon</label>
              <input
                type="text"
                className="form-control"
                value={settings.store_phone}
                onChange={(e) =>
                  setSettings({ ...settings, store_phone: e.target.value })
                }
              />
            </div>
          </div>
        </div>
        {/* end kolom kiri */}

        {/* === KOLOM KANAN === */}
        <div className="col-12 col-lg-6">
          {/* TAMPILAN */}
          <div className="card p-4 mb-3">
            <h6 className="fw-bold mb-3">Tampilan</h6>
            <div className="mb-3">
              <label className="form-label">Warna Sidebar</label>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="color"
                  className="form-control form-control-color flex-shrink-0"
                  style={{ width: 42, height: 38, padding: 2 }}
                  value={settings.sidebar_color || "#0f172a"}
                  onChange={(e) => {
                    setSettings({ ...settings, sidebar_color: e.target.value });
                    applySidebarColor(e.target.value);
                  }}
                />
                <input
                  type="text"
                  className="form-control font-monospace"
                  placeholder="#0f172a"
                  value={settings.sidebar_color}
                  onChange={(e) => {
                    setSettings({ ...settings, sidebar_color: e.target.value });
                    if (/^#[0-9a-f]{6}$/i.test(e.target.value))
                      applySidebarColor(e.target.value);
                  }}
                />
                {settings.sidebar_color && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary flex-shrink-0"
                    onClick={() => {
                      setSettings({ ...settings, sidebar_color: "" });
                      [
                        "--sidebar-bg",
                        "--sidebar-text",
                        "--sidebar-label",
                        "--sidebar-active-text",
                        "--sidebar-active",
                      ].forEach((v) =>
                        document.documentElement.style.removeProperty(v),
                      );
                    }}
                  >
                    Reset
                  </button>
                )}
              </div>
              <div className="form-text">Kosongkan untuk warna bawaan.</div>
            </div>
            <div className="mb-0">
              <label className="form-label">
                Warna Primer (tombol &amp; aksen)
              </label>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="color"
                  className="form-control form-control-color flex-shrink-0"
                  style={{ width: 42, height: 38, padding: 2 }}
                  value={settings.primary_color || "#4f46e5"}
                  onChange={(e) => {
                    setSettings({ ...settings, primary_color: e.target.value });
                    document.documentElement.style.setProperty(
                      "--primary",
                      e.target.value,
                    );
                    document.documentElement.style.setProperty(
                      "--primary-dark",
                      e.target.value,
                    );
                  }}
                />
                <input
                  type="text"
                  className="form-control font-monospace"
                  placeholder="#4f46e5"
                  value={settings.primary_color}
                  onChange={(e) => {
                    setSettings({ ...settings, primary_color: e.target.value });
                    if (/^#[0-9a-f]{6}$/i.test(e.target.value)) {
                      document.documentElement.style.setProperty(
                        "--primary",
                        e.target.value,
                      );
                      document.documentElement.style.setProperty(
                        "--primary-dark",
                        e.target.value,
                      );
                    }
                  }}
                />
                {settings.primary_color && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary flex-shrink-0"
                    onClick={() => {
                      setSettings({ ...settings, primary_color: "" });
                      document.documentElement.style.removeProperty(
                        "--primary",
                      );
                      document.documentElement.style.removeProperty(
                        "--primary-dark",
                      );
                    }}
                  >
                    Reset
                  </button>
                )}
              </div>
              <div className="form-text">Kosongkan untuk warna bawaan.</div>
            </div>
          </div>

          {/* KONFIGURASI */}
          <div className="card p-4 mb-3">
            <h6 className="fw-bold mb-3">Konfigurasi</h6>
            <div className="row g-3 mb-3">
              <div className="col-sm-6">
                <label className="form-label">Persentase Pajak (%)</label>
                <input
                  type="number"
                  className="form-control"
                  value={settings.tax_percentage}
                  onChange={(e) =>
                    setSettings({ ...settings, tax_percentage: e.target.value })
                  }
                />
              </div>
              <div className="col-sm-6">
                <label className="form-label">Alert Stok Minimum</label>
                <input
                  type="number"
                  className="form-control"
                  value={settings.min_stock_alert}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      min_stock_alert: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="mb-0">
              <label className="form-label">Footer Struk</label>
              <textarea
                className="form-control"
                rows="3"
                value={settings.receipt_footer}
                onChange={(e) =>
                  setSettings({ ...settings, receipt_footer: e.target.value })
                }
              />
            </div>
          </div>
        </div>
        {/* end kolom kanan */}
      </div>
      {/* end row */}

      <button
        className="btn btn-primary px-4"
        onClick={handleSave}
        disabled={loading}
      >
        {loading ? "Menyimpan..." : "Simpan Pengaturan"}
      </button>
    </div>
  );
};

export default Settings;
